import React, { useState, useEffect } from "react";
import { UserCircle, Mail, Phone, Calendar, IdCard, User } from "lucide-react";


interface UserProfile {
    pin: string;
    phone_number: string;
    email: string;
    id_series: string;
    id_number: string;
    first_name: string;
    last_name: string;
    father_name: string;
    gender: string;
    birth_date: string;
}

const ProfilePage: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile>({
        pin: "",
        phone_number: "",
        email: "",
        id_series: "",
        id_number: "",
        first_name: "",
        last_name: "",
        father_name: "",
        gender: "",
        birth_date: "",
    });

  const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';


  const translations = {
        az: {
            son: "oğlu",
            male: "Kişi",
            female: "Qadın",
            personalInfo: "Şəxsi Məlumatlar",
            pinCode: "Ş.V fin kod",
            idCard: "Ş.V seriya nömrəsi",
            birthDate: "Doğum tarixi",
            gender: "Cinsiyyət",
            contactInfo: "Əlaqə Məlumatları",
            phoneNumber: "Telefon nömrəsi",
            email: "E-poçt"
        },
        ru: {
            son: "сын",
            male: "Мужчина",
            female: "Женщина",
            personalInfo: "Личная информация",
            pinCode: "ПИН-код",
            idCard: "Серия и номер удостоверения",
            birthDate: "Дата рождения",
            gender: "Пол",
            contactInfo: "Контактная информация",
            phoneNumber: "Номер телефона",
            email: "Эл. почта"
        },
        en: {
            son: "'s son",
            male: "Male",
            female: "Female",
            personalInfo: "Personal Information",
            pinCode: "Personal ID Code",
            idCard: "ID Card Number",
            birthDate: "Birth Date",
            gender: "Gender",
            contactInfo: "Contact Information",
            phoneNumber: "Phone Number",
            email: "Email"
        }
    };

    const t = translations[currentLanguage as keyof typeof translations];
  useEffect(() => {
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      const decoded = JSON.parse(userInfo);
      setProfile({
        pin: decoded.pin || "",
        phone_number: decoded.phone_number || "",
        email: decoded.email || "",
        id_series: decoded.id_series || "",
        id_number: decoded.id_number || "",
        first_name: decoded.first_name || "",
        last_name: decoded.last_name || "",
        father_name: decoded.father_name || "",
        gender: decoded.gender || "",
        birth_date: decoded.birth_date || "",
      });
    }
  }, []);

    const formatDate = (dateString: any) => {
        if (!dateString) return null;

        const match = dateString.match(/(\d{4})\s*M(\d{2})\s*(\d{2})/);
        if (match) {
            const [_, year, month, day] = match;
            return `${day}.${month}.${year}`;
        }

        try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}.${month}.${year}`;
            }
        } catch (error) {
            return null;
        }

        return null;
    };

    const getGenderText = (gender: string) => {
        return gender === "M" ? t.male : gender === "F" ? t.female : "";
    };

    return (
        <div className="">
            <div className=" mx-auto">
                {/* Header Section */}
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
                    <div className="flex flex-col sm:flex-row items-center  space-y-4 sm:space-y-0 sm:space-x-6">
                        <div className="w-24 h-24 bg-colormainbrown rounded-full flex items-center justify-center">
                            <UserCircle className="w-16 h-16 text-white" />
                        </div>
                        <div className="text-center sm:text-left flex-1  ">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                {profile.first_name} {profile.last_name}
                            </h1>
                            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                    {getGenderText(profile.gender)}
                                </span>
                                {profile.birth_date && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                        {formatDate(profile.birth_date)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <User className="w-5 h-5 mr-2 text-colormainbrown" />
                        {t.personalInfo}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* PIN */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <IdCard className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.pinCode}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {profile.pin || "—"}
                                </p>
                            </div>
                        </div>

                        {/* ID Card */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <IdCard className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.idCard}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {profile.id_series && profile.id_number
                                        ? `${profile.id_series}${profile.id_number}`
                                        : "—"}
                                </p>
                            </div>
                        </div>

                        {/* Birth Date */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.birthDate}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {formatDate(profile.birth_date) || "—"}
                                </p>
                            </div>
                        </div>

                        {/* Gender */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.gender}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {getGenderText(profile.gender) || "—"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <Phone className="w-5 h-5 mr-2 text-colormainbrown" />
                        {t.contactInfo}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Phone */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-orange-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.phoneNumber}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {profile.phone_number ? `+${profile.phone_number}` : "—"}
                                </p>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500">{t.email}</p>
                                <p className="text-base font-semibold text-gray-900 truncate">
                                    {profile.email || "—"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;